import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CreateUserModal from "./createUserModal";
import UpdateUserModal from "./updateUserModal";
import axios from "axios";
import {
  HomeIcon,
  UserGroupIcon,
  PlusIcon,
  SearchIcon,
  PencilIcon,
  TrashIcon,
  FilterIcon,
  DotsVerticalIcon,
  ViewGridIcon,
  ViewListIcon,
  MailIcon,
  BadgeCheckIcon,
} from "@heroicons/react/outline";

const Users = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selecteduser_id, setSelecteduser_id] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(5);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list"); // 'grid' or 'list'
  const [activeMenu, setActiveMenu] = useState(null);

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;

  // Filter users based on search
  const filteredUsers = users.filter((user) =>
    user.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openUpdateModal = (user_id) => {
    setIsUpdateModalOpen(true);
    setSelecteduser_id(user_id);
    setActiveMenu(null);
  };

  const closeUpdateModal = () => {
    setIsUpdateModalOpen(false);
    setSelecteduser_id(null);
    setSelectedUser(null);
  };

  const handleDeleteUser = async (user_id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const accessToken = localStorage.getItem("accessToken");
        await axios.delete(`${baseUrl}/user/${user_id}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        fetchUsers();
        setActiveMenu(null);
      } catch (error) {
        console.error("Error deleting user:", error);
      }
    }
  };

  // Fetch users once when the page mounts.
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await axios.get(`${baseUrl}/user`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setUsers(response.data.data.users);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const getUserInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const avatarColors = [
    'from-blue-400 to-blue-600',
    'from-purple-400 to-purple-600',
    'from-pink-400 to-pink-600',
    'from-green-400 to-green-600',
    'from-orange-400 to-orange-600',
  ];

  return (
    <div className="users-page">
      {/* Header */}
      <div className="page-header">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/dashboard" className="breadcrumb-item">
            <HomeIcon className="w-4 h-4" />
            <span>Dashboard</span>
          </Link>
          <svg className="breadcrumb-separator" viewBox="0 0 6 10">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4" />
          </svg>
          <span className="breadcrumb-item-active">Users</span>
        </nav>

        {/* Page Title and Action */}
        <div className="title-section">
          <div>
            <h1 className="page-title">User Management</h1>
            <p className="page-subtitle">Manage your team members and their roles</p>
          </div>
          <button onClick={openModal} className="create-user-btn">
            <PlusIcon className="w-5 h-5" />
            <span>Create New User</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-cards">
        <div className="stat-card stat-card-purple">
          <div className="stat-icon">
            <UserGroupIcon className="w-6 h-6" />
          </div>
          <div>
            <p className="stat-label">Total Users</p>
            <h3 className="stat-value">{users.length}</h3>
          </div>
        </div>
        <div className="stat-card stat-card-blue">
          <div className="stat-icon">
            <BadgeCheckIcon className="w-6 h-6" />
          </div>
          <div>
            <p className="stat-label">Active Users</p>
            <h3 className="stat-value">{users.length}</h3>
          </div>
        </div>
        <div className="stat-card stat-card-green">
          <div className="stat-icon">
            <MailIcon className="w-6 h-6" />
          </div>
          <div>
            <p className="stat-label">Verified</p>
            <h3 className="stat-value">{users.length}</h3>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="filters-section">
        {/* Search Bar */}
        <div className="search-wrapper">
          <SearchIcon className="search-icon" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-actions">
          <div className="results-count">
            <span>{filteredUsers.length} {filteredUsers.length === 1 ? 'User' : 'Users'}</span>
          </div>

          <button className="filter-btn">
            <FilterIcon className="w-4 h-4" />
            <span>Filter</span>
          </button>

          {/* View Toggle */}
          <div className="view-toggle">
            <button
              onClick={() => setViewMode("list")}
              className={`toggle-btn ${viewMode === "list" ? "active" : ""}`}
            >
              <ViewListIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
            >
              <ViewGridIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Users Display */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      ) : viewMode === "list" ? (
        /* List View */
        <div className="table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" className="checkbox" />
                </th>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((user, idx) => (
                <tr key={user.user_id}>
                  <td>
                    <input type="checkbox" className="checkbox" />
                  </td>
                  <td>
                    <div className="user-cell">
                      <div className={`user-avatar bg-gradient-to-br ${avatarColors[idx % avatarColors.length]}`}>
                        {getUserInitials(user.first_name, user.last_name)}
                      </div>
                      <div>
                        <div className="user-name">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="user-id">ID: {user.user_id}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="email-cell">
                      <MailIcon className="w-4 h-4 text-gray-400" />
                      <span>{user.email}</span>
                    </div>
                  </td>
                  <td>
                    <span className="role-badge">
                      {Array.isArray(user.role) ? "No Role" : user.role.role_name}
                    </span>
                  </td>
                  <td>
                    <span className="status-badge status-active">Active</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => openUpdateModal(user.user_id)}
                        className="action-btn action-btn-edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.user_id)}
                        className="action-btn action-btn-delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {currentUsers.length === 0 && (
            <div className="empty-state">
              <UserGroupIcon className="w-16 h-16 text-gray-400" />
              <h3>No Users Found</h3>
              <p>
                {searchQuery
                  ? "Try adjusting your search criteria"
                  : "Get started by creating your first user"}
              </p>
            </div>
          )}
        </div>
      ) : (
        /* Grid View */
        <div className="grid-view">
          {currentUsers.map((user, idx) => (
            <div key={user.user_id} className="user-card">
              <div className="user-card-header">
                <div className={`user-card-avatar bg-gradient-to-br ${avatarColors[idx % avatarColors.length]}`}>
                  {getUserInitials(user.first_name, user.last_name)}
                </div>
                <div className="user-card-menu">
                  <button
                    onClick={() => setActiveMenu(activeMenu === user.user_id ? null : user.user_id)}
                    className="menu-btn"
                  >
                    <DotsVerticalIcon className="w-5 h-5" />
                  </button>
                  {activeMenu === user.user_id && (
                    <div className="dropdown-menu">
                      <button onClick={() => openUpdateModal(user.user_id)}>
                        <PencilIcon className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                      <button onClick={() => handleDeleteUser(user.user_id)}>
                        <TrashIcon className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="user-card-body">
                <h3 className="user-card-name">
                  {user.first_name} {user.last_name}
                </h3>
                <p className="user-card-email">{user.email}</p>
                <div className="user-card-info">
                  <span className="role-badge">
                    {Array.isArray(user.role) ? "No Role" : user.role.role_name}
                  </span>
                  <span className="status-badge status-active">Active</span>
                </div>
              </div>
            </div>
          ))}

          {currentUsers.length === 0 && (
            <div className="empty-state grid-empty">
              <UserGroupIcon className="w-16 h-16 text-gray-400" />
              <h3>No Users Found</h3>
              <p>
                {searchQuery
                  ? "Try adjusting your search criteria"
                  : "Get started by creating your first user"}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {filteredUsers.length > usersPerPage && (
        <div className="pagination">
          <div className="pagination-info">
            Showing {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length}
          </div>
          <div className="pagination-buttons">
            {Array.from(
              { length: Math.ceil(filteredUsers.length / usersPerPage) },
              (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                >
                  {i + 1}
                </button>
              )
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateUserModal
        isOpen={isModalOpen}
        onClose={closeModal}
        fetchUsers={fetchUsers}
      />
      <UpdateUserModal
        isOpen={isUpdateModalOpen}
        onClose={closeUpdateModal}
        user_id={selecteduser_id}
        fetchUsers={fetchUsers}
        user={selectedUser}
      />

      {/* Styles */}
      <style jsx>{`
        .users-page {
          padding: 0;
        }

        .page-header {
          margin-bottom: 2rem;
        }

        .breadcrumb {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
        }

        .breadcrumb-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #6b7280;
          text-decoration: none;
          transition: color 0.2s;
        }

        .breadcrumb-item:hover {
          color: #8b5cf6;
        }

        .breadcrumb-separator {
          width: 0.75rem;
          height: 0.75rem;
          fill: none;
          stroke: #9ca3af;
        }

        .breadcrumb-item-active {
          font-weight: 600;
          color: #1f2937;
        }

        .title-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .page-subtitle {
          font-size: 1rem;
          color: #6b7280;
          margin: 0;
        }

        .create-user-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          font-weight: 600;
          border-radius: 0.75rem;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.3);
        }

        .create-user-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.4);
        }

        .stats-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 1rem;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }

        .stat-icon {
          width: 3rem;
          height: 3rem;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .stat-card-purple .stat-icon {
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
        }

        .stat-card-blue .stat-icon {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
        }

        .stat-card-green .stat-icon {
          background: linear-gradient(135deg, #10b981, #059669);
        }

        .stat-label {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 0.25rem 0;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0;
        }

        .filters-section {
          background: white;
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          margin-bottom: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .search-wrapper {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          width: 1.25rem;
          height: 1.25rem;
          color: #9ca3af;
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 3rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #8b5cf6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .filter-actions {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .results-count {
          padding: 0.5rem 1rem;
          background: #f3f4f6;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .filter-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .filter-btn:hover {
          border-color: #8b5cf6;
          color: #8b5cf6;
        }

        .view-toggle {
          display: flex;
          background: #f3f4f6;
          border-radius: 0.5rem;
          padding: 0.25rem;
        }

        .toggle-btn {
          padding: 0.5rem;
          background: transparent;
          border: none;
          border-radius: 0.375rem;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .toggle-btn.active {
          background: white;
          color: #8b5cf6;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
        }

        .spinner {
          width: 3rem;
          height: 3rem;
          border: 4px solid #e5e7eb;
          border-top-color: #8b5cf6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .table-container {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        .users-table {
          width: 100%;
          border-collapse: collapse;
        }

        .users-table thead {
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .users-table th {
          padding: 1rem 1.5rem;
          text-align: left;
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .users-table td {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f3f4f6;
        }

        .users-table tbody tr {
          transition: background 0.2s;
        }

        .users-table tbody tr:hover {
          background: #f9fafb;
        }

        .checkbox {
          width: 1rem;
          height: 1rem;
          border-radius: 0.25rem;
          cursor: pointer;
        }

        .user-cell {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .user-avatar {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 0.875rem;
        }

        .user-name {
          font-weight: 600;
          color: #1f2937;
        }

        .user-id {
          font-size: 0.75rem;
          color: #9ca3af;
        }

        .email-cell {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #6b7280;
        }

        .role-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: #eff6ff;
          color: #2563eb;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .status-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .status-active {
          background: #d1fae5;
          color: #065f46;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          padding: 0.5rem;
          border: none;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .action-btn-edit {
          background: #eff6ff;
          color: #2563eb;
        }

        .action-btn-edit:hover {
          background: #dbeafe;
          transform: scale(1.1);
        }

        .action-btn-delete {
          background: #fef2f2;
          color: #dc2626;
        }

        .action-btn-delete:hover {
          background: #fee2e2;
          transform: scale(1.1);
        }

        .grid-view {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.5rem;
        }

        .user-card {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .user-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }

        .user-card-header {
          padding: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          background: linear-gradient(135deg, #f9fafb, #ffffff);
        }

        .user-card-avatar {
          width: 4rem;
          height: 4rem;
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 1.25rem;
        }

        .user-card-menu {
          position: relative;
        }

        .menu-btn {
          padding: 0.25rem;
          background: transparent;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          border-radius: 0.375rem;
          transition: all 0.2s ease;
        }

        .menu-btn:hover {
          background: #f3f4f6;
          color: #1f2937;
        }

        .dropdown-menu {
          position: absolute;
          right: 0;
          top: 100%;
          margin-top: 0.5rem;
          background: white;
          border-radius: 0.5rem;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          min-width: 150px;
          z-index: 10;
        }

        .dropdown-menu button {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: none;
          border: none;
          text-align: left;
          font-size: 0.875rem;
          color: #374151;
          cursor: pointer;
          transition: background 0.2s;
        }

        .dropdown-menu button:hover {
          background: #f9fafb;
        }

        .dropdown-menu button:last-child {
          color: #dc2626;
        }

        .user-card-body {
          padding: 1.5rem;
          padding-top: 0;
        }

        .user-card-name {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .user-card-email {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 1rem 0;
        }

        .user-card-info {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .empty-state {
          padding: 4rem 2rem;
          text-align: center;
        }

        .empty-state h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
          margin: 1rem 0 0.5rem 0;
        }

        .empty-state p {
          color: #6b7280;
          margin: 0;
        }

        .grid-empty {
          grid-column: 1 / -1;
        }

        .pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 1.5rem;
          padding: 1rem;
          background: white;
          border-radius: 0.75rem;
          border: 1px solid #e5e7eb;
        }

        .pagination-info {
          font-size: 0.875rem;
          color: #6b7280;
          font-weight: 500;
        }

        .pagination-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .page-btn {
          padding: 0.5rem 1rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .page-btn:hover {
          border-color: #8b5cf6;
          color: #8b5cf6;
        }

        .page-btn.active {
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          border-color: transparent;
          color: white;
        }

        @media (max-width: 1024px) {
          .title-section {
            flex-direction: column;
            align-items: flex-start;
          }

          .create-user-btn {
            width: 100%;
          }

          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-wrapper {
            max-width: 100%;
          }

          .filter-actions {
            justify-content: space-between;
          }
        }

        @media (max-width: 768px) {
          .page-title {
            font-size: 1.5rem;
          }

          .stats-cards {
            grid-template-columns: 1fr;
          }

          .users-table {
            display: block;
            overflow-x: auto;
          }

          .pagination {
            flex-direction: column;
            gap: 1rem;
          }

          .grid-view {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Users;
